/**
 * 도메인 타입.
 *
 * 여기 열거형은 기획서가 '고정값'으로 못 박은 것들입니다 (design/tokens.json 의 domain 절).
 * 값을 늘리거나 줄이는 것은 기획 변경이므로, 코드에서 임의로 바꾸지 마세요.
 */

export const PRAYER_STATUS = [
  'PRAYING',
  'IN_PROGRESS',
  'PARTIAL',
  'ANSWERED',
  'ON_HOLD',
  'CLOSED',
] as const;
export type PrayerStatus = (typeof PRAYER_STATUS)[number];

export const PRIORITY = ['URGENT', 'IMPORTANT', 'NORMAL'] as const;
export type Priority = (typeof PRIORITY)[number];

export const ROOM_CARD_TYPE = ['VERSE_SHARING', 'DECISION', 'RECORDING'] as const;
export type RoomCardType = (typeof ROOM_CARD_TYPE)[number];

/**
 * 영역 — 앱이 정한 여섯 (설계 2026-08-27, 화면 C1-1).
 *
 * 예전에는 사용자가 만들고 이름을 바꾸고 지우는 서랍이었고 그 안에 세부항목이
 * 또 있었습니다. 지금은 노트가 가진 값 하나입니다 — 영역은 노트를 담는 곳이
 * 아니라 노트의 항목이고, 그래서 탐색이 아니라 조건으로 다룹니다.
 *
 * 화면에 적히는 이름은 «영역»이지만 통신과 DB 의 키는 category 그대로입니다.
 * 뜻이 같은 말로 바꾸자고 이미 한 번 깨뜨린 약속을 또 깨뜨릴 이유가 없습니다.
 *
 * server-spring 의 Rules.CATEGORIES · DB 의 check 제약과 같은 값이어야 합니다.
 */
export const CATEGORIES = ['개인', '가정', '지인', '교회', '사회', '공동체'] as const;
export type CategoryName = (typeof CATEGORIES)[number];

export const statusLabel: Record<PrayerStatus, string> = {
  PRAYING: '기도중',
  IN_PROGRESS: '진행중',
  PARTIAL: '부분응답',
  ANSWERED: '응답완료',
  ON_HOLD: '보류',
  CLOSED: '종료',
};

export const priorityLabel: Record<Priority, string> = {
  URGENT: '긴급',
  IMPORTANT: '중요',
  NORMAL: '일반',
};

export const cardTypeLabel: Record<RoomCardType, string> = {
  VERSE_SHARING: '말씀 나눔',
  DECISION: '기도 노트(결단)',
  RECORDING: '녹음',
};

/** 코멘트 글자수 상한 — 기획서 3.5 */
export const COMMENT_MAX_LENGTH = 150;

/**
 * 노트에 남기는 이모티콘 — 기획서 3.5.
 *
 * 다섯으로 좁혔습니다. 기도 노트에 남기는 반응이라 «무엇이라 말할까»의 폭이
 * 넓지 않고, 골라야 할 것이 많으면 누르지 않게 됩니다.
 *
 * **한 사람이 한 노트에 하나**입니다. 다른 것을 누르면 갈아타고, 같은 것을
 * 다시 누르면 뗍니다 — 「어떻게 반응했나」이지 「몇 번 눌렀나」가 아닙니다.
 *
 * server-spring 의 Rules.EMOJIS 와 같은 값이어야 합니다.
 */
export const EMOJIS = ['🙏', '❤️', '🙌', '✨', '😢'] as const;
export type Emoji = (typeof EMOJIS)[number];

/** 이모티콘을 눌렀을 때 곁들이는 말 — 길게 눌러 확인하는 자리에 씁니다 */
export const emojiLabel: Record<Emoji, string> = {
  '🙏': '함께 기도합니다',
  '❤️': '마음을 보냅니다',
  '🙌': '응답을 함께 기뻐합니다',
  '✨': '감사합니다',
  '😢': '함께 아파합니다',
};

export interface EmojiCount {
  emoji: string;
  count: number;
  /** 내가 누른 것인가 — 화면이 스스로 세지 않게 서버가 정해 보냅니다 */
  mine: boolean;
}

export interface Comment {
  reactionId: string;
  authorId: string;
  authorName?: string;
  body: string;
  /** 지울 수 있는가 — 남긴 사람과 노트 주인입니다 */
  canRemove: boolean;
  createdAt: string;
}

/** 노트에 달린 반응 전부 — 화면 F1-1 */
export interface Reactions {
  emojis: EmojiCount[];
  comments: Comment[];
}

/** C4 공유 시트의 한 줄 — 고를 수 있는 친구와 지금 가 있는지 */
export interface ShareTarget {
  userId: string;
  name?: string;
  shared: boolean;
}

/**
 * Plan.
 * EVERYDAY 는 Prayer Diary 에 나타나지 않고 D-day 계산도 하지 않습니다 (기획서 3.4).
 */
export type Plan = { type: 'EVERYDAY' } | { type: 'DATE'; date: string };

/** 대표 기도제목 상한 — 확정 3번 */
export const MAX_REPRESENTATIVE = 3;

/**
 * 기도 진행 기록 — 사용자가 직접 쌓습니다.
 * 항목이 제목·날짜·내용인 것은 감사한 일을 바로 적을 수 있게 하기 위함입니다.
 */
export interface ProgressEntry {
  entryId: string;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  body: string;
}

/** 기도 히스토리 — 시스템이 쌓는 변경 로그. 사용자가 손대지 못합니다. */
export interface HistoryEntry {
  at: string;
  kind: string;
  detail?: string;
}

export interface PrayerNote {
  id: string;
  title: string;
  body: string;
  category: string;
  status: PrayerStatus;
  priority: Priority;
  plan: Plan;
  /** 대표 기도제목 on/off. 기본 off, 최대 3개 — 기획서 3.2.1 · 확정 3번 */
  representative: boolean;
  /** 비공개. 켜면 서로 친구에게도 공개 노트로 보이지 않습니다 */
  isPrivate: boolean;
  progress: ProgressEntry[];
  history: HistoryEntry[];
  author: string;
  createdAt: string;
  updatedAt: string;
}

/* ── 친구 — D1 · D2 · D2-1 · D3 ──────────────────────────────── */

/** 목록과 줄 머리에 쓰는 사람 한 명. 이름이 없을 수 있습니다(닉네임 미수신) */
export interface Person {
  userId: string;
  name: string | null;
}

export type FriendState = 'ACTIVE' | 'BLOCKED';

export interface Friend extends Person {
  state: FriendState;
  /** 리스트 상단에 고정 — 화면 D3 */
  pinned: boolean;
  /**
   * 서로 친구이고 차단이 없는가.
   *
   * **대표 기도제목이 비었는지와 다른 일입니다.** 친구인데 대표를 아직 안
   * 정한 사람도 있습니다 — D2-1(아직 친구가 아닙니다)은 이 값으로 가립니다.
   */
  mutual: boolean;
  /** 팝업(D2)에서만 채워집니다. 목록(D1)에서는 없습니다 */
  representative?: PrayerNote[];
}

/* ── 모임과 기록지 — F1 ~ F4 ─────────────────────────────────── */

/**
 * 모임 기본 사진의 키 — 화면 F2-3.
 *
 * 새벽 · 산 · 바다처럼 **글자 없는 그림**이라 어떤 모임 이름과 나란히 놓여도
 * 겹쳐 읽히지 않습니다. 고르지 않으면 목록이 모임 이름 앞 두 글자를 씁니다.
 *
 * server-spring 의 Rules.GROUP_PHOTOS 와 같은 값이어야 합니다.
 */
export const GROUP_PHOTOS = [
  'dawn', 'mount', 'sea', 'field', 'vine', 'candle',
  'night', 'olive', 'road', 'cloud', 'wheat', 'ray',
] as const;
export type GroupPhoto = (typeof GROUP_PHOTOS)[number];

export interface Group {
  groupId: string;
  name: string;
  intro: string;
  photo?: GroupPhoto;
  ownerId: string;
  /**
   * 그룹장인가 — **서버가 정해 보냅니다.**
   *
   * ownerId 와 내 식별자를 화면이 비교하게 두면 같은 판단이 두 곳에 생깁니다.
   * 수정 · 구성원 편집 · 기록지 만들기 · 삭제가 전부 이 값에 달립니다.
   */
  canManage: boolean;
  members: Person[];
  /** 목록(F1)의 부제 — "7월 4째주 순모임 · 7/22" */
  latest?: string;
  updatedAt: string;
}

export type ItemKind = 'VERSE_SHARING' | 'PRAYER_NOTE' | 'DECISION';

export const itemLabel: Record<ItemKind, string> = {
  VERSE_SHARING: '말씀 나눔',
  PRAYER_NOTE: '기도 노트',
  DECISION: '결단',
};

/** 직접 적기 · 대표 기도제목 공유 — 화면 F4-2 */
export type EntryMode = 'WRITTEN' | 'SHARED';

/** 기도 노트 · 결단의 «한 사람 한 줄» */
export interface Entry {
  entryId: string;
  member: Person;
  mode: EntryMode;
  title: string;
  body: string;
  /** 그룹장이 대신 적었으면 그 사람 — 「그룹장 ○○이 적음」 (F4-2-1) */
  writtenBy?: Person;
  /** 이어진 노트. 원본을 따라 바뀝니다 — 공유한 대표이거나 Prayer Book 에 담은 것 */
  notes: PrayerNote[];
  /** 그룹장은 모든 줄, 그룹원은 자기 줄만 — 서버가 정합니다 */
  canEdit: boolean;
  /** 적힌 것이 있는가 — 구성원을 뺄 때 한 번 더 물을지 (F4-4-1-1) */
  written: boolean;
}

export interface Item {
  itemId: string;
  kind: ItemKind;
  sortOrder: number;
  /* 말씀 나눔 전용. 나머지 항목에서는 빈 문자열입니다 */
  verse: string;
  interpretation: string;
  questions: string;
  /** 기도 노트 · 결단의 사람별 줄. 말씀 나눔은 빈 배열입니다 */
  entries: Entry[];
}

export interface Sheet {
  sheetId: string;
  groupId: string;
  title: string;
  /** 모임 날짜. 만든 날이 아니라 모인 날입니다 */
  metOn: string;
  participants: Person[];
  items: Item[];
  canManage: boolean;
  updatedAt: string;
}

/** 목록(F3)에 쓰는 가벼운 기록지 */
export interface SheetBrief {
  sheetId: string;
  title: string;
  metOn: string;
  /** "요한복음 15:1–8 · 기도 노트 4 · 결단 6" — 적힌 줄만 셉니다 */
  summary: string;
}

/**
 * 「7월 5째주 순모임」 — 화면 F3-3 이 날짜에 맞춰 미리 채우는 제목.
 *
 * 몇째 주는 **그 달 1일이 든 주를 1주로** 셉니다. 사람이 달력을 보고 "이번
 * 주가 몇째 주"라고 말할 때 쓰는 셈법입니다(ISO 주차가 아닙니다).
 * server-spring 의 weekTitle 과 같은 셈이어야 합니다 — 화면이 미리 채운 제목과
 * 서버가 채우는 제목이 다르면 아무것도 손대지 않았는데 제목이 바뀝니다.
 */
export function weekTitle(date: string): string {
  const day = new Date(`${date}T00:00:00`);
  const first = new Date(day.getFullYear(), day.getMonth(), 1);
  const week = Math.floor((day.getDate() + first.getDay() - 1) / 7) + 1;
  return `${day.getMonth() + 1}월 ${week}째주 순모임`;
}

/**
 * 오늘로부터 남은 일수. Everyday 노트는 대상이 아닙니다.
 * 날짜만 비교하도록 양쪽을 자정으로 내려 시간 성분을 제거합니다.
 */
export function daysUntil(date: string, today = new Date()): number {
  const target = new Date(`${date}T00:00:00`);
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - base.getTime()) / 86_400_000);
}

/** D-day 표기. 지난 날짜는 D+n 으로 보여줍니다. */
export function ddayLabel(date: string, today = new Date()): string {
  const d = daysUntil(date, today);
  if (d === 0) return 'D-DAY';
  return d > 0 ? `D-${d}` : `D+${-d}`;
}

/* ── 목록 정렬 — C1 · C4 ─────────────────────────────────────── */

export const SORT_KEYS = ['recent', 'status'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const sortLabel: Record<SortKey, string> = {
  recent: '최신순',
  status: '상태순',
};

/**
 * 방향은 오름/내림이 아니라 '기본(↓)'과 '뒤집기(↑)'입니다.
 *
 * 둘이 같은 부호를 뜻하지 않기 때문입니다 — 최신순 ↓ 는 updatedAt 내림차순이고,
 * 상태순 ↓ 는 기도중부터라 순서값 오름차순입니다. 화면에는 화살표만 보이므로
 * 코드도 화살표 쪽을 들고 있는 편이 헷갈리지 않습니다.
 */
export type SortDir = 'down' | 'up';

export interface Sort {
  key: SortKey;
  dir: SortDir;
}

/** 목록에 들어오면 최신순 ↓ 입니다 */
export const DEFAULT_SORT: Sort = { key: 'recent', dir: 'down' };

/** 다른 것을 누르면 그 정렬의 ↓, 같은 것을 다시 누르면 방향만 뒤집힙니다 */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key !== key) return { key, dir: 'down' };
  return { key, dir: current.dir === 'down' ? 'up' : 'down' };
}

/** 상태순 ↓ 의 차례. 기획서가 정한 순서라 임의로 바꾸지 마세요 */
const STATUS_ORDER: Record<PrayerStatus, number> = {
  PRAYING: 0,
  IN_PROGRESS: 1,
  PARTIAL: 2,
  ANSWERED: 3,
  ON_HOLD: 4,
  CLOSED: 5,
};

export function sortNotes(notes: PrayerNote[], sort: Sort): PrayerNote[] {
  const flip = sort.dir === 'down' ? 1 : -1;
  // 같은 값끼리는 언제나 최신 것이 위로. 목록이 매번 다른 순서로 흔들리지 않게 합니다
  const byRecent = (a: PrayerNote, b: PrayerNote) => b.updatedAt.localeCompare(a.updatedAt);
  const list = [...notes];

  if (sort.key === 'recent') return list.sort((a, b) => flip * byRecent(a, b));

  return list.sort(
    (a, b) => flip * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || byRecent(a, b),
  );
}

/* ── 목록 조건 — C1-1 ~ C1-4 ───────────────────────────────── */

/**
 * 조건 넷.
 *
 * 넷은 서로 독립입니다 — 켜진 것이 늘수록 목록이 좁아집니다 (화면 C1-1-1).
 * 'ALL' 이 그 조건을 쓰지 않는 상태이고, 초기화는 전부 'ALL' 로 되돌립니다.
 *
 * 서버로 보내지 않고 화면에서 겁니다. 조건을 켰다 껐다 할 때마다 목록이
 * 서버를 다녀오면 펼침이 닫히는 사이에 화면이 비고, 한 사람의 노트는
 * 통째로 들고 있어도 부담이 없는 크기입니다.
 */
export const ALL = 'ALL' as const;

export interface Filter {
  category: CategoryName | typeof ALL;
  status: PrayerStatus | typeof ALL;
  priority: Priority | typeof ALL;
  /** 대표는 켜기/끄기 하나라 펼칠 것이 없습니다 — 버튼을 누르면 바로 걸립니다 */
  representativeOnly: boolean;
}

export const NO_FILTER: Filter = {
  category: ALL,
  status: ALL,
  priority: ALL,
  representativeOnly: false,
};

/**
 * 「필터」버튼이 켜져 보여야 하는가 — 하나라도 걸려 있으면 켜집니다.
 *
 * **검색어는 여기 들어오지 않습니다.** 검색은 조건 줄에도 시트에도 없어서,
 * 글자를 치는 동안 「필터」가 켜지면 초기화를 눌러도 안 꺼지는 버튼이 됩니다.
 */
export function isFiltered(filter: Filter): boolean {
  return (
    filter.category !== ALL ||
    filter.status !== ALL ||
    filter.priority !== ALL ||
    filter.representativeOnly
  );
}

export function filterNotes(notes: PrayerNote[], filter: Filter): PrayerNote[] {
  return notes.filter(
    (n) =>
      (filter.category === ALL || n.category === filter.category) &&
      (filter.status === ALL || n.status === filter.status) &&
      (filter.priority === ALL || n.priority === filter.priority) &&
      (!filter.representativeOnly || n.representative),
  );
}

/**
 * 제목 · 내용 검색 — C1 목록 맨 위의 창.
 *
 * **걸린 조건 안에서 찾습니다.** 검색한다고 조건이 풀리지 않으므로 이 함수는
 * filterNotes 가 걸러낸 것을 받습니다 — 머리글의 숫자와 켜진 버튼이 그대로
 * 남아, 지금 어느 범위를 뒤지는 중인지 보입니다 (화면 C1).
 *
 * 대소문자를 가리지 않습니다. 한글에는 뜻이 없지만 영어 제목이 섞이고,
 * 가리는 편이 나은 이유가 없습니다.
 */
export function searchNotes(notes: PrayerNote[], query: string): PrayerNote[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return notes;
  return notes.filter(
    (n) =>
      n.title.toLowerCase().includes(needle) || n.body.toLowerCase().includes(needle),
  );
}

/**
 * 머리글의 이름 — '전체 기도 노트 34' / '개인 기도 노트 12' (화면 C1-1-1).
 *
 * 영역만 이름을 바꿉니다. 나머지 조건은 숫자로 드러납니다 —
 * '기도중 기도 노트'처럼 적으면 읽기가 어색하고, 조건을 여럿 걸었을 때
 * 이름 하나로 다 담을 수도 없습니다.
 */
export function scopeLabel(filter: Filter): string {
  return filter.category === ALL ? '전체' : filter.category;
}
